<template>
  <SCADropdown
    :ref="setDropdown"
    v-model:is-open="isOpen"
    match-trigger-width="min"
    :no-mobile-margin="noMobileMargin"
    :class="[$style.dropdown, { [$style.hovered]: hasTagSlot }]"
    @opened="$emit('opened')"
    @closed="$emit('closed')"
  >
    <template #trigger>
      <SCAInputBase
        :appearance="showsBareTag ? 'no-border' : appearance"
        :placeholder="placeholder"
        :disabled="disabled"
        :underline-override="true"
        :class="{ [$style.inert]: disabled && !!$slots.input }"
        @click="onTriggerClick"
        @pointerdown="handlePointerDown"
      >
        <template v-if="$slots.triggerContent">
          <slot name="triggerContent" />
        </template>
        <template v-else>
          <SCASvgIcon v-if="icon" :icon="icon" size="16px" />
          <SCAAvatar
            v-if="selectedItem?.avatarInitials"
            size="x3small"
            :initials="selectedItem.avatarInitials[0]"
            class="me-component-gap-xSmall"
          />
          <SCATag
            v-if="showClosedColorTag && hasTagSlot"
            tabindex="-1"
            :class="[
              $style.tag,
              $style.active,
              {
                [$style.noHover]: disableHoverColorTag,
                [$style.unsetPadding]: disableHoverColorTag && isTransparentBg,
                [$style.truncate]: truncateClosedTag,
              },
            ]"
            :truncate-tooltip="truncateClosedTag"
          >
            {{ selectedItem?.label }}
          </SCATag>
          <div
            v-if="$slots.input"
            ref="inputSlot"
            :class="$style.inputSlot"
            @focusin="onInputSlotFocusIn"
            @focusout="onInputSlotFocusOut"
            @keydown.enter="onInputSlotEnter"
          >
            <input v-if="disabled" disabled tabindex="-1" aria-hidden="true" :class="$style.styleProbe" />
            <slot name="input" />
          </div>
          <input
            v-else
            :id="htmlFor"
            :value="triggerInputValue"
            :placeholder="placeholder"
            :disabled="disabled"
            :readonly="!search"
            @input="handleSearchTermChange"
            @keydown="handleKeyDown"
            @focus="onFocus"
            @blur="hasFocus = false"
          />
          <SCASvgIcon
            v-if="!disabled"
            icon="phosphor/caret-down"
            :class="[$style.iconColor, $style.arrow, { [$style.arrowOpen]: isOpen }]"
            size="12px"
          />
          <slot name="after" />
        </template>
      </SCAInputBase>
    </template>

    <template #default>
      <div ref="itemsWrapper" :class="$style.itemsWrapper" @scroll="onScroll">
        <SCADropdownItem
          v-for="(item, index) in filteredItems"
          :key="item.id"
          :label="item.label"
          :class="[
            $style.items,
            { [$style.selectedItem]: item.id === modelValue },
            { [$style.highlighted]: isHighlighted(index) },
            { [$style.unset]: hasTagSlot },
          ]"
          :icon="item.icon"
          :icon-size="itemIconSize"
          :avatar-initials="item.avatarInitials"
          :disabled="item.disabled"
          :unset-all="hasTagSlot"
          :use-smaller-vertical-padding="useSmallerVerticalPadding"
          type="action"
          @mousedown.prevent
          @click="selectItem(item.id)"
        >
          <template v-if="$slots.avatar" #avatar>
            <slot name="avatar" :item="item" />
          </template>
          <template v-if="$slots.tag" #tag>
            <slot name="tag" :item="item" />
          </template>
        </SCADropdownItem>
        <SCANoItems v-if="filteredItems.length === 0" />
      </div>
    </template>
  </SCADropdown>
</template>

<script setup lang="ts">
import { computed, ref, useSlots, useTemplateRef, watch } from "vue";

import SCADropdown from "./Dropdown/SCADropdown.vue";
import SCADropdownItem from "./Dropdown/SCADropdownItem.vue";
import type { IconName } from "./icons/iconNames.generated";
import SCAAvatar from "./SCAAvatar.vue";
import SCAInputBase from "./SCAInputBase.vue";
import SCASvgIcon from "./SCASvgIcon.vue";
import SCATag from "./SCATag.vue";
import SCANoItems from "./table/SCANoItems.vue";
import type { InputAppearance, SelectableItem } from "./types";
import { useDropdownHandle, useDropdownKeyboard } from "./useDropdownKeyboard";

const props = withDefaults(
  defineProps<{
    modelValue?: SelectableItem["id"];
    items: SelectableItem[];
    search?: "client" | "custom";
    searchTerm?: string;
    disabled?: boolean;
    placeholder?: string;
    icon?: IconName;
    htmlFor?: string;
    alert?: boolean;
    hidePopoverOnValueChange?: boolean;
    appearance?: InputAppearance;
    hasMore?: boolean;
    loadingMore?: boolean;
    disableHoverColorTag?: boolean;
    useSmallerVerticalPadding?: boolean;
    itemIconSize?: string;
    noMobileMargin?: boolean;
    truncateClosedTag?: boolean;
  }>(),
  {
    modelValue: undefined,
    disabled: false,
    search: undefined,
    searchTerm: "",
    placeholder: undefined,
    icon: undefined,
    htmlFor: undefined,
    alert: false,
    hidePopoverOnValueChange: true,
    appearance: undefined,
    hasMore: false,
    loadingMore: false,
    disableHoverColorTag: false,
    useSmallerVerticalPadding: false,
    itemIconSize: undefined,
    noMobileMargin: false,
    truncateClosedTag: false,
  },
);

const emit = defineEmits<{
  "update:modelValue": [value: SelectableItem["id"]];
  "update:searchTerm": [searchTerm: string];
  opened: [];
  closed: [];
  loadMore: [];
}>();

const isOpen = ref(false);
const searchTerm = ref(props.searchTerm || "");
const slots = useSlots();
const hasTagSlot = computed(() => !!slots.tag);

const itemsWrapperRef = useTemplateRef<HTMLElement>("itemsWrapper");
const { setDropdown, hideList } = useDropdownHandle();

const selectedItem = computed(() => props.items.find((i) => i.id === props.modelValue));
const selectedIndex = computed(() => filteredItems.value.findIndex((i) => i.id === props.modelValue));
const showClosedColorTag = computed(() => !!selectedItem.value?.color && !isOpen.value);

const hasFocus = ref(false);

function onFocus() {
  hasFocus.value = true;
  handleFocus();
}

const showsBareTag = computed(
  () => hasTagSlot.value && !!selectedItem.value?.label && !isOpen.value && !hasFocus.value,
);

const triggerInputValue = computed(() => {
  if (showClosedColorTag.value) return "";
  if (isOpen.value && props.search) return searchTerm.value;
  return selectedItem.value?.label ?? "";
});

const tagBgColor = computed(
  () => selectedItem.value?.color?.bgColor ?? "var(--color-interaction-neutral-subtle-normal)",
);
const tagTextColor = computed(() => selectedItem.value?.color?.textColor ?? "var(--color-content-primary)");
const hoverBgColor = computed(() => selectedItem.value?.color?.hoverBgColor);
const hoverTextColor = computed(() => selectedItem.value?.color?.hoverTextColor);
const isTransparentBg = computed(() => selectedItem.value?.color?.bgColor === "transparent");

const filteredItems = computed(() => {
  const visible = props.items.filter((item) => !item.hidden);
  if (!props.search || props.search === "custom") {
    return visible;
  }
  return visible.filter((item) => item.label.toLocaleLowerCase().includes(searchTerm.value.toLocaleLowerCase()));
});

watch(
  () => props.searchTerm,
  (newVal) => {
    if (newVal !== searchTerm.value) {
      searchTerm.value = newVal || "";
    }
  },
);

watch(searchTerm, (val) => {
  emit("update:searchTerm", val);
});

watch(
  () => props.modelValue,
  () => {
    if (props.hidePopoverOnValueChange) {
      isOpen.value = false;
      searchTerm.value = "";
    }
  },
);

function handleSearchTermChange(event: Event) {
  searchTerm.value = (event.target as HTMLInputElement).value;
  if (!isOpen.value && !props.disabled) {
    isOpen.value = true;
  }
}

/**
 * Commits an option, from a click or from Enter.
 */
function selectItem(id: SelectableItem["id"]) {
  if (props.disabled || props.items.find((item) => item.id === id)?.disabled) return;
  emit("update:modelValue", id);
  searchTerm.value = "";
  isOpen.value = false;
}

const { isHighlighted, handleKeyDown, handleFocus, handlePointerDown } = useDropdownKeyboard<SelectableItem>({
  isOpen,
  items: filteredItems,
  listEl: itemsWrapperRef,
  searchTerm,
  open: openDropdown,
  commit: (item: SelectableItem) => selectItem(item.id),
  isInert: () => props.disabled,
  initialIndex: () => selectedIndex.value,
  hideList,
});

/**
 * Opens the list, on click and on focus alike.
 *
 * Opening on focus is what lets the keyboard drive a select without a detour: Tab lands on it and the
 * options are already there, ready for the arrow keys `useDropdownKeyboard` handles. Nothing has to be
 * pressed first to make the field usable.
 *
 * An empty list is not opened — a popover holding only "no items" is noise, and the click path has
 * always guarded against it.
 */
function openDropdown() {
  if (props.disabled) return;
  if (filteredItems.value.length > 0 || searchTerm.value) {
    isOpen.value = true;
  }
}

const inputSlotRef = useTemplateRef<HTMLElement>("inputSlot");

function isWithinInputSlot(node: EventTarget | null): boolean {
  return node instanceof Node && !!inputSlotRef.value?.contains(node);
}

function onInputSlotFocusIn(event: FocusEvent) {
  if (isWithinInputSlot(event.relatedTarget)) return;
  onFocus();
}

function onInputSlotFocusOut(event: FocusEvent) {
  if (isWithinInputSlot(event.relatedTarget)) return;
  hasFocus.value = false;
  hideList();
  isOpen.value = false;
}

function onInputSlotEnter(event: KeyboardEvent) {
  if (!isOpen.value) return;
  event.preventDefault();
  hideList();
  isOpen.value = false;
}

function onTriggerClick() {
  openDropdown();
  const slotEl = inputSlotRef.value;
  if (!slotEl || props.disabled || slotEl.contains(document.activeElement)) return;
  slotEl.querySelector<HTMLElement>('input:not([tabindex="-1"])')?.focus();
}

function onScroll() {
  const el = itemsWrapperRef.value;
  if (!el || !props.hasMore || props.loadingMore) return;
  if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) {
    emit("loadMore");
  }
}
</script>

<style module lang="scss">
@use "../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;
@use "../styles/icon.scss" as IconMixins;

.dropdown {
  width: 100%;

  &:hover .arrow {
    opacity: 1;
  }
}

.hovered:hover {
  box-shadow: inset 0px -1px 0px 0px var(--color-blue-p1);
  cursor: pointer;
}

.itemsWrapper {
  overflow-y: auto;
  max-height: 300px;
  overscroll-behavior: contain;
}
.items {
  display: flex;
  width: 100%;
  border-radius: var(--border-radius-large);

  &:not(:first-child) {
    margin-top: var(--space-component-padding-xSmall);
  }

  &.highlighted {
    background-color: var(--color-interaction-ghost-hover);
  }

  &.selectedItem {
    background-color: var(--color-background-informative-onSubtle);
    color: var(--color-content-brand-primary, #113997);
  }

  &.unset:not(.highlighted):not(.selectedItem) {
    background-color: transparent;
  }
}

.alert {
  border-radius: var(--border-radius-large);
  border: 1px solid var(--color-interaction-border-alert) !important;
}

.inputSlot {
  position: relative;
  display: flex;
  flex: 1 1;
  min-width: 0;
  align-items: center;
}

.styleProbe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  opacity: 0;
  pointer-events: none;
}

.inert {
  pointer-events: none;
}

.iconColor {
  @include IconMixins.icon-color(var(--color-interaction-border-hover));
}

.arrow {
  flex-shrink: 0;
  opacity: 0;
  transition:
    opacity 0.3s ease,
    transform 0.3s ease,
    color 0.3s ease;
}

.arrowOpen {
  opacity: 1;
  @include IconMixins.icon-color(var(--color-content-brand-primary));
  transform: rotate(180deg);
}

.tag {
  height: 20px !important;
  padding: 2px 8px;
  background-color: v-bind(tagBgColor);
  color: v-bind(tagTextColor);
  min-width: fit-content;
  transition:
    background-color 0.2s,
    color 0.2s;

  &.unsetPadding {
    padding: 2px 0;
  }
}

.tag + input {
  flex: 0 0 0;
  min-width: 0;
}

.active {
  &:hover {
    background-color: v-bind(hoverBgColor);
    color: v-bind(hoverTextColor);
    cursor: pointer;
  }
}

.active.noHover:hover {
  background-color: v-bind(tagBgColor);
  color: v-bind(tagTextColor);
}

.truncate {
  min-width: 0;
  max-width: 100%;
  flex-shrink: 0;
}
</style>
